/*
 * ******************************************************************
 * Copyright (c) 2021-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { CloudProviderEnum, ServiceTypeEnum, TaskStatesEnum, TasksV2SubTypesEnum, TasksV2TypesEnum, TaskV2 } from "@api/generated/vcdr";
import { L10nService } from "@vmw/ngx-vip";
import { CspHeaderAppAlertConfig, CspHeaderAppAlertPriority, CspHeaderAppAlertType, CspHeaderNotificationConfig, CspHeaderNotificationState } from "@vmw/csp-ngx-components/header";
import { OperationInitType } from "@app/services/dispatcher/events/operation-events";
import { StatesUtil } from "@shared/utils/states.util";

export class TaskNotificationInfo {
    public progressTitle: string;
    public progressDescription: string;
    public completedTitle: string;
    public failedTitle: string;
}

export class NotificationUtils {
    // todo: localize the separator and move to constants
    public static ERRORS_SEPARATOR: string = "'";
    private static alertsCount = 0;

    // TODO: research id and messageId - can they help up to filter repeating InAppNotifications

    public static createFailedTaskNotification(task: TaskV2,
                                               isNew: boolean,
                                               l10nService: L10nService): CspHeaderNotificationConfig {
        const result: CspHeaderNotificationConfig = new CspHeaderNotificationConfig();
        result.id = task.id;
        result.title = l10nService.getMessage(NotificationUtils.getTaskFailedTitle(task));
        result.date = new Date(task.end_time);
        result.state = CspHeaderNotificationState.FAILED;
        result.description = NotificationUtils.issuesToString(task);
        result.isNew = isNew;
        return result;
    }

    public static createSuccessfulTaskNotification(task: TaskV2,
                                                   isNew: boolean,
                                                   l10nService: L10nService): CspHeaderNotificationConfig {
        const result: CspHeaderNotificationConfig = new CspHeaderNotificationConfig();
        result.id = task.id;
        result.title = l10nService.getMessage(NotificationUtils.getTaskCompletedTitle(task));
        result.description = l10nService.getMessage(
            NotificationUtils.getTaskCompletedDescription(task),
            [task.service_type, this.getResourceId(task), task.created_by]);
        result.date = new Date(task.end_time);
        result.state = task.state === TaskStatesEnum.DONE ? CspHeaderNotificationState.SUCCEEDED
            : CspHeaderNotificationState.INFO;
        result.isNew = isNew;
        return result;
    }

    public static createErrorNotification(title: string,
                                          description: string,
                                          primaryButtonTitle?: string,
                                          primaryActionId?: string): CspHeaderNotificationConfig {
        return NotificationUtils.createNotification(
            CspHeaderNotificationState.FAILED,
            title,
            description,
            primaryButtonTitle,
            primaryActionId);
    }

    public static createSuccessNotification(title: string,
                                            description: string,
                                            primaryButtonTitle?: string,
                                            primaryActionId?: string): CspHeaderNotificationConfig {
        return NotificationUtils.createNotification(
            CspHeaderNotificationState.SUCCEEDED,
            title,
            description,
            primaryButtonTitle,
            primaryActionId);
    }

    public static getTaskNotificationInfo(task: TaskV2, l10nService: L10nService): TaskNotificationInfo {
        let taskInfo = new TaskNotificationInfo();

        const serviceTypeKey = StatesUtil.serviceTypeToString(task.vcds_service_type);
        const serviceType = l10nService.getMessage(serviceTypeKey);
        const taskSpec: any = task.task_spec;

        switch (task.type) {
            case TasksV2TypesEnum.DEPLOYMENT:
                if (task.sub_type === TasksV2SubTypesEnum.ACTIVATE) {
                    if (this.isOnPremDeployment(task)) {
                        taskInfo.progressTitle = l10nService.getMessage("console.task.progress.provision.onprem");
                        const deploymentName: string = this.getDeploymentName(task);
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.provision.onprem.description",
                            [deploymentName]);
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.provision.onprem",
                            [deploymentName]);
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.provision.onprem",
                            [deploymentName]);
                    } else {
                        taskInfo.progressTitle = l10nService.getMessage("console.task.progress.provision",
                                                                        [serviceType]);
                        const deploymentName: string = this.getDeploymentName(task);
                        const providerName: string = this.getProviderName(task) ? "- " + l10nService.getMessage(this.getProviderName(
                            task)) : "";
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.provision.description",
                            [serviceType, providerName, deploymentName]);
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.provision",
                            [serviceType, providerName, deploymentName]);
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.provision",
                            [serviceType, providerName, deploymentName]);
                    }
                } else if (task.sub_type === TasksV2SubTypesEnum.DEACTIVATE) {
                    if (this.isOnPremDeployment(task)) {
                        taskInfo.progressTitle = l10nService.getMessage("console.task.progress.deactivate.onprem");
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.deactivate.onprem.description");
                        taskInfo.completedTitle = l10nService.getMessage("console.task.success.deactivate.onprem");
                        taskInfo.failedTitle = l10nService.getMessage("console.task.failure.deactivate.onprem");
                    } else {
                        taskInfo.progressTitle = l10nService.getMessage(
                            "console.task.progress.deactivate",
                            [serviceType]);
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.deactivate.description",
                            [serviceType]);
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.deactivate",
                            [serviceType]);
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.deactivate",
                            [serviceType]);
                    }
                } else {
                    taskInfo = null;
                }
                break;
            case TasksV2TypesEnum.DISCONNECTED_DEPLOYMENT:
                if (task.sub_type === TasksV2SubTypesEnum.ACTIVATE) {
                    taskInfo.progressTitle = l10nService.getMessage("console.task.progress.provision.disconnected",
                        [serviceType]);
                    const deploymentName = this.getDisconnectedDeploymentName(task);
                    taskInfo.progressDescription = l10nService.getMessage(
                        "console.task.progress.provision.disconnected.description",
                        [serviceType, deploymentName]);
                    taskInfo.completedTitle = l10nService.getMessage(
                        "console.task.success.provision.disconnected",
                        [serviceType, deploymentName]);
                    taskInfo.failedTitle = l10nService.getMessage(
                        "console.task.failure.provision.disconnected",
                        [serviceType, deploymentName]);
                } else if (task.sub_type === TasksV2SubTypesEnum.DEACTIVATE) {
                    taskInfo.progressTitle = l10nService.getMessage("console.task.progress.deactivate.disconnected",
                        [serviceType]);
                    const deploymentName = this.getDisconnectedDeploymentName(task);
                    taskInfo.progressDescription = l10nService.getMessage(
                        "console.task.progress.deactivate.disconnected.description",
                        [serviceType, deploymentName]);
                    taskInfo.completedTitle = l10nService.getMessage(
                        "console.task.success.deactivate.disconnected",
                        [serviceType, deploymentName]);
                    taskInfo.failedTitle = l10nService.getMessage(
                        "console.task.failure.deactivate.disconnected",
                        [serviceType, deploymentName]);
                } else {
                    taskInfo = null;
                }
                break;
            case TasksV2TypesEnum.ACCESS:
                taskInfo.progressTitle = l10nService.getMessage("console.task.progress.request.access");
                taskInfo.progressDescription = l10nService.getMessage(
                    "console.task.progress.request.access.description", [serviceType]);
                taskInfo.completedTitle = l10nService.getMessage("console.task.success.request.access",
                    [serviceType]);
                taskInfo.failedTitle = l10nService.getMessage("console.task.failure.request.access",
                    [serviceType]);
                break;
            case TasksV2TypesEnum.RANSOMWARE_RECOVERY:
                if (task.sub_type === TasksV2SubTypesEnum.ACTIVATE) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.ransomware.recovery.activation",
                        [serviceType]);
                    if (taskSpec) {
                        const region = this.getRwrRegion(task);
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.ransomware.recovery.activation.description",
                            [region]);
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.ransomware.recovery.activation",
                            [region]);
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.ransomware.recovery.activation.param",
                            [region]);
                    }
                } else if (task.sub_type === TasksV2SubTypesEnum.DEACTIVATE) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.ransomware.recovery.deactivation",
                        [serviceType]);
                    if (taskSpec) {
                        const region = this.getRwrRegion(task);
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.ransomware.recovery.deactivation.description",
                            [region]);
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.ransomware.recovery.deactivation",
                            [region]);
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.ransomware.recovery.deactivation.param",
                            [region]);
                    }
                } else if (task.sub_type === TasksV2SubTypesEnum.EDR_ORG_PROVISION) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.ransomware.recovery.provision");
                    taskInfo.progressDescription = l10nService.getMessage(
                        "console.task.progress.ransomware.recovery.provision.description");
                    taskInfo.completedTitle = l10nService.getMessage(
                        "console.task.success.ransomware.recovery.provision");
                } else if (task.sub_type === TasksV2SubTypesEnum.EDR_ORG_DEPROVISION) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.ransomware.recovery.deprovision");
                    taskInfo.progressDescription = l10nService.getMessage(
                        "console.task.progress.ransomware.recovery.deprovision.description");
                    taskInfo.completedTitle = l10nService.getMessage(
                        "console.task.success.ransomware.recovery.deprovision");
                } else if (task.sub_type === TasksV2SubTypesEnum.EDR_ORG_CREATE_API_KEY) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.ransomware.recovery.createApiKey");
                    taskInfo.progressDescription = l10nService.getMessage(
                        "console.task.progress.ransomware.recovery.createApiKey.description");
                    taskInfo.completedTitle = l10nService.getMessage(
                        "console.task.success.ransomware.recovery.createApiKey");
                } else if (task.sub_type === TasksV2SubTypesEnum.EDR_ORG_DELETE_API_KEY) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.ransomware.recovery.deleteApiKey");
                    taskInfo.progressDescription = l10nService.getMessage(
                        "console.task.progress.ransomware.recovery.deleteApiKey.description");
                    taskInfo.completedTitle = l10nService.getMessage(
                        "console.task.success.ransomware.recovery.deleteApiKey");
                } else {
                    taskInfo = null;
                }
                break;
            case TasksV2TypesEnum.VCF_AUTHORIZATION:
                if (task.sub_type === TasksV2SubTypesEnum.CREATE) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.vcf.oauthapp.creation");
                    if (taskSpec) {
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.vcf.oauthapp.creation.description");
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.vcf.oauthapp.creation");
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.vcf.oauthapp.creation");
                    }
                } else {
                    taskInfo = null;
                }
                break;
            case TasksV2TypesEnum.VMC_AUTHORIZATION:
                if (task.sub_type === TasksV2SubTypesEnum.CREATE) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.vmc.oauthapp.creation");
                    if (taskSpec) {
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.vmc.oauthapp.creation.description");
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.vmc.oauthapp.creation");
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.vmc.oauthapp.creation");
                    }
                } else {
                    taskInfo = null;
                }
                break;
            case TasksV2TypesEnum.RESOURCE_POOL:
                if (task.sub_type === TasksV2SubTypesEnum.ASSIGN_VMCA) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.vmc.org.link");
                    if (taskSpec) {
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.vmc.org.link.description");
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.vmc.org.link");
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.vmc.org.link");
                    }
                } else if (task.sub_type === TasksV2SubTypesEnum.ASSIGN_GCVE) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.gcve.org.link");
                    if (taskSpec) {
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.gcve.org.link.description");
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.gcve.org.link");
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.gcve.org.link");
                    }
                } else if (task.sub_type === TasksV2SubTypesEnum.ASSIGN_EVS) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.evs.org.link");
                    if (taskSpec) {
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.evs.org.link.description");
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.evs.org.link");
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.evs.org.link");
                    }
                } else if (task.sub_type === TasksV2SubTypesEnum.UNASSIGN_VMCA) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.vmc.org.unlink");
                    if (taskSpec) {
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.vmc.org.unlink.description");
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.vmc.org.unlink");
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.vmc.org.unlink");
                    }
                } else if (task.sub_type === TasksV2SubTypesEnum.UNASSIGN_GCVE) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.gcve.org.unlink");
                    if (taskSpec) {
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.gcve.org.unlink.description");
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.gcve.org.unlink");
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.gcve.org.unlink");
                    }
                } else if (task.sub_type === TasksV2SubTypesEnum.UNASSIGN_EVS) {
                    taskInfo.progressTitle = l10nService.getMessage(
                        "console.task.progress.evs.org.unlink");
                    if (taskSpec) {
                        taskInfo.progressDescription = l10nService.getMessage(
                            "console.task.progress.evs.org.unlink.description");
                        taskInfo.completedTitle = l10nService.getMessage(
                            "console.task.success.evs.org.unlink");
                        taskInfo.failedTitle = l10nService.getMessage(
                            "console.task.failure.evs.org.unlink");
                    }
                } else {
                    taskInfo = null;
                }
                break;
            default:
                return null;
        }
        return taskInfo;
    }

    public static getTaskCompletedDescription(task: TaskV2): string {
        switch (task.type) {
            default:
                // No handling for any task type for now.
                return null;
        }
    }

    public static getTaskCompletedTitle(task: TaskV2): string {
        switch (task.type) {
            case TasksV2TypesEnum.DEPLOYMENT:
                if (task.sub_type === TasksV2SubTypesEnum.ACTIVATE) {
                    if (task.service_type === ServiceTypeEnum.VCDR) {
                        return "vlr.console.notifications.success.provision.vlcr";
                    } else if (task.service_type === ServiceTypeEnum.VMCFS) {
                        return "vmcfs.console.notifications.success.provision.vmcfs";
                    }
                } else if (task.sub_type === TasksV2SubTypesEnum.DEACTIVATE) {
                    if (task.service_type === ServiceTypeEnum.VCDR) {
                        return "vlr.console.notifications.success.deactivate.vlcr";
                    } else if (task.service_type === ServiceTypeEnum.VMCFS) {
                        return "vmcfs.console.notifications.success.deactivate.vmcfs";
                    }
                }
                break;
            case TasksV2TypesEnum.ACCESS:
                return "vlr.console.notifications.success.request.access";
            case TasksV2TypesEnum.RANSOMWARE_RECOVERY:
                if (task.sub_type === TasksV2SubTypesEnum.ACTIVATE) {
                    return "vlr.console.notifications.success.ransomware.recovery.activation";
                } else if (task.sub_type === TasksV2SubTypesEnum.DEACTIVATE) {
                    return "vlr.console.notifications.success.ransomware.recovery.deactivation";
                } else if (task.sub_type === TasksV2SubTypesEnum.EDR_ORG_PROVISION) {
                    return "vlr.console.notifications.success.ransomware.recovery.provision";
                } else if (task.sub_type === TasksV2SubTypesEnum.EDR_ORG_DEPROVISION) {
                    return "vlr.console.notifications.success.ransomware.recovery.deprovision";
                } else if (task.sub_type === TasksV2SubTypesEnum.EDR_ORG_CREATE_API_KEY) {
                    return "vlr.console.notifications.success.ransomware.recovery.createApiKey";
                } else if (task.sub_type === TasksV2SubTypesEnum.EDR_ORG_DELETE_API_KEY) {
                    return "vlr.console.notifications.success.ransomware.recovery.deleteApiKey";
                }
                break;
            case TasksV2TypesEnum.MIGRATE:
                return "console.notifications.success.migration";
            case TasksV2TypesEnum.RELEASE:
                if (task.sub_type === TasksV2SubTypesEnum.ADD) {
                    return "console.notifications.success.add.release.vlcr";
                } else if (task.sub_type === TasksV2SubTypesEnum.UPDATE) {
                    return "console.notifications.success.update.release.vlcr";
                }
                break;
            default:
                return "console.notifications.success.operation";
        }
        return "console.notifications.success.operation";
    }

    public static getTaskFailedTitle(task: TaskV2): string {
        switch (task.type) {
            case TasksV2TypesEnum.DEPLOYMENT:
                if (task.sub_type === TasksV2SubTypesEnum.ACTIVATE) {
                    if (task.service_type === ServiceTypeEnum.VCDR) {
                        return "vlr.console.notifications.failure.provision.vlcr";
                    } else if (task.service_type === ServiceTypeEnum.VMCFS) {
                        return "vmcfs.console.notifications.failure.provision.vmcfs";
                    }
                } else if (task.sub_type === TasksV2SubTypesEnum.DEACTIVATE) {
                    if (task.service_type === ServiceTypeEnum.VCDR) {
                        return "vlr.console.notifications.failure.deactivate.vlcr";
                    } else if (task.service_type === ServiceTypeEnum.VMCFS) {
                        return "vmcfs.console.notifications.failure.deactivate.vmcfs";
                    }
                }
                break;
            case TasksV2TypesEnum.ACCESS:
                return "vlr.console.notifications.failure.request.access";
            case TasksV2TypesEnum.RANSOMWARE_RECOVERY:
                if (task.sub_type === TasksV2SubTypesEnum.ACTIVATE) {
                    return "vlr.console.notifications.failure.ransomware.recovery.activation";
                } else if (task.sub_type === TasksV2SubTypesEnum.DEACTIVATE) {
                    return "vlr.console.notifications.failure.ransomware.recovery.deactivation";
                } else if (task.sub_type === TasksV2SubTypesEnum.EDR_ORG_PROVISION) {
                    return "vlr.console.notifications.failure.ransomware.recovery.provision";
                } else if (task.sub_type === TasksV2SubTypesEnum.EDR_ORG_DEPROVISION) {
                    return "vlr.console.notifications.failure.ransomware.recovery.deprovision";
                } else if (task.sub_type === TasksV2SubTypesEnum.EDR_ORG_CREATE_API_KEY) {
                    return "vlr.console.notifications.failure.ransomware.recovery.createApiKey";
                } else if (task.sub_type === TasksV2SubTypesEnum.EDR_ORG_DELETE_API_KEY) {
                    return "vlr.console.notifications.failure.ransomware.recovery.deleteApiKey";
                }
                break;
            case TasksV2TypesEnum.MIGRATE:
                return "console.notifications.failure.migration";
            case TasksV2TypesEnum.RELEASE:
                if (task.sub_type === TasksV2SubTypesEnum.ADD) {
                    return "console.notifications.failure.add.release.vlcr";
                } else if (task.sub_type === TasksV2SubTypesEnum.UPDATE) {
                    return "console.notifications.failure.update.release.vlcr";
                }
                break;
            default:
                return "console.notifications.failure.operation";
        }
        return "console.notifications.failure.operation";
    }

    public static createAlert(type: CspHeaderAppAlertType,
                              priority: CspHeaderAppAlertPriority,
                              content: string): CspHeaderAppAlertConfig {

        const result: CspHeaderAppAlertConfig = new CspHeaderAppAlertConfig();
        result.id = "dpsAlertId_" + (++this.alertsCount);
        result.closable = false; // todo - do we need this to be parameterized?
        result.remove = false; // todo - do we need this to be parameterized?

        result.type = type;
        result.priority = priority;
        result.content = content;

        return result;
    }

    private static createNotification(state: CspHeaderNotificationState,
                                      title: string,
                                      description: string,
                                      primaryButtonTitle?: string,
                                      primaryActionId?: string): CspHeaderNotificationConfig {
        const result: CspHeaderNotificationConfig = new CspHeaderNotificationConfig();
        result.state = state;
        result.title = title;
        result.description = description;
        result.date = new Date();
        result.primaryButtonTitle = primaryButtonTitle;
        result.primaryActionId = primaryActionId;
        return result;
    }

    private static issuesToString(task: TaskV2): string {
        let result: string = "";
        if (task?.data?.errors?.length > 0) {
            const errorMsgs = task.data.errors.map(err => err.error_message);
            result = errorMsgs.join(NotificationUtils.ERRORS_SEPARATOR);
        }
        if (task?.data?.warnings?.length > 0) {
            const warningMsgs = task.data.warnings.map(warning => warning.error_message);
            result = result + NotificationUtils.ERRORS_SEPARATOR +
                warningMsgs.join(NotificationUtils.ERRORS_SEPARATOR);
        }
        return result;
    }

    private static isOnPremDeployment(task: TaskV2): boolean {
        const taskSpec: any = task.task_spec;
        return taskSpec?.capabilities && !!taskSpec.capabilities.vlrOnPrem && !taskSpec.capabilities.vlrCloud;
    }

    private static getDeploymentName(task: TaskV2): string {
        let name = "";
        const taskSpec: any = task.task_spec;
        if (taskSpec?.name) {
            name = taskSpec.name;
        }
        return name;
    }

    private static getProviderName(task: TaskV2): string {
        let name = "";
        const taskSpec: any = task.task_spec;
        if (taskSpec?.provider) {
            if (taskSpec.provider === CloudProviderEnum.AWS) {
                name = "console.cloudprovider.aws";
            }
            if (taskSpec.provider === CloudProviderEnum.GCP) {
                name = "console.cloudprovider.gcp";
            }
        }
        return name;
    }

    private static getDisconnectedDeploymentName(task: TaskV2): string {
        let name = "";
        const taskSpec: any = task.task_spec;
        if (taskSpec?.components?.length > 1 &&
            taskSpec?.components[0].name &&
            taskSpec?.components[1].name) {
            name = taskSpec?.components[0].name + " - " + taskSpec?.components[1].name;
        }
        return name;
    }

    public static getStrings(task: TaskV2): TaskNotificationStrings {
        if (task.type === TasksV2TypesEnum.DEPLOYMENT && task.sub_type === TasksV2SubTypesEnum.UPGRADE) {
            return new UpgradeTaskStrings();
        }
        return null;
    }

    public static getResourceId(task: TaskV2): string {
        if (!task) {
            return null;
        }
        return task.resource_id;
    }

    private static getRwrRegion(task: TaskV2): string {
        const taskSpec: any = task.task_spec;
        if (taskSpec?.region) {
            return taskSpec.region;
        }
        if (taskSpec?.vlcr_region) {
            return taskSpec.vlcr_region;
        }
        return "";
    }
}

export interface TaskNotificationStrings {
    opInitType: OperationInitType;
    errorTitlePartial: string;
    errorTitle: string;
    errorDescriptionSome: string;
    errorDescriptionAll: string;
    errorDescriptionOne: string;
    errorDescriptionUnknown: string;
    successTitle: string;
    successDescriptionAll: string;
    successDescriptionOne: string;
    successDescriptionUnknown: string;
}

export class UpgradeTaskStrings implements TaskNotificationStrings {
    public opInitType = OperationInitType.UPGRADE;
    public errorTitlePartial = "op.console.upgrade.instance.error.partial";
    public errorTitle = "op.console.upgrade.instance.error";
    public errorDescriptionSome = "op.console.upgrade.instance.error.some";
    public errorDescriptionAll = "op.console.upgrade.instance.error.all";
    public errorDescriptionOne = "op.console.upgrade.instance.error.one";
    public errorDescriptionUnknown = "op.console.upgrade.instance.error.unknown";
    public successTitle = "op.console.upgrade.instance.success";
    public successDescriptionAll = "op.console.upgrade.instance.success.all";
    public successDescriptionOne = "op.console.upgrade.instance.success.one";
    public successDescriptionUnknown = "op.console.upgrade.instance.success.unknown";
}
